import type { GmRewardPerk, GmStatusKey } from '../types/gm-statuses';
import type { GroupedPlayerRewardDto } from '@/queries/playerGamificationRewards/playerGamificationRewards.schema';
import type { ExtendedProgressStatusDto } from '@/queries/progress-statuses/progress-statuses.schema';
import { GamificationRewardType } from '@/queries/playerGamificationRewards/playerGamificationRewards.schema';
import { statusKeys } from '../constants/statuses';
import { getBenefitLockBadges } from '../constants/statusRewardBadgePolicy';
import { STATUS_REWARD_BENEFITS } from '../constants/statusRewardData';
import { getStatusKey } from './getStatusKey';

export const FIRST_PLAYER_LEVEL = 1;

export type BenefitAvailability = 'available' | 'needs-status' | 'coming-soon' | 'unavailable';

export interface ResolvedStatusRewardBenefit {
  benefit: (typeof STATUS_REWARD_BENEFITS)[number];
  availability: BenefitAvailability;
  statusName?: string;
  groupedReward?: GroupedPlayerRewardDto;
  badges: ReturnType<typeof getBenefitLockBadges>;
}

type BenefitWithoutBadges = Omit<ResolvedStatusRewardBenefit, 'badges'>;

interface Params {
  currentStatusKey?: GmStatusKey;
  groupedRewards?: GroupedPlayerRewardDto[];
  progressStatuses?: ExtendedProgressStatusDto[];
  playerLevel: number;
}

/**
 * Used for all statues list/table
 * used for reward page grid
 */
export const buildStatusRewardBenefits = ({ currentStatusKey, groupedRewards, progressStatuses, playerLevel }: Params): ResolvedStatusRewardBenefit[] => {
  const benefits: BenefitWithoutBadges[] = STATUS_REWARD_BENEFITS.map((benefit) => {
    if (benefit.type) {
      const groupedReward = groupedRewards?.find(reward => reward.type === benefit.type);
      const isFirstLevelCard = benefit.type === GamificationRewardType.LevelUp && playerLevel === FIRST_PLAYER_LEVEL;
      const requiredLevel = groupedReward?.minLevelFrom;
      const statusName = progressStatuses?.find(status => status.levelFrom === requiredLevel)?.name;

      return {
        benefit,
        groupedReward,
        availability: isFirstLevelCard || (!!requiredLevel && playerLevel >= requiredLevel)
          ? 'available'
          : requiredLevel ? 'needs-status' : 'unavailable',
        statusName,
      };
    }

    if (!benefit.unlocksWithStatus) {
      return { benefit, availability: 'coming-soon' };
    }

    const requiredStatusKey = statusKeys.find(key => benefit.values[key]);
    if (!requiredStatusKey) {
      return { benefit, availability: 'coming-soon' };
    }

    const isAvailable = currentStatusKey
      && statusKeys.indexOf(currentStatusKey) >= statusKeys.indexOf(requiredStatusKey);

    const statusName = progressStatuses?.find(status => getStatusKey(status) === requiredStatusKey)?.name;

    return {
      benefit,
      availability: isAvailable ? 'available' : 'needs-status',
      statusName: isAvailable ? undefined : statusName,
    };
  });

  return benefits.map(item => ({
    ...item,
    badges: getBenefitLockBadges({ id: item.benefit.id, availability: item.availability, statusName: item.statusName }),
  }));
};

/**
 * Used for building mobile status reward perks
 */
export const buildStatusRewardPerks = (benefits: ResolvedStatusRewardBenefit[], statusKey?: GmStatusKey) => {
  if (!statusKey) {
    return [];
  }

  return benefits.map(({ benefit, availability, badges }) => {
    const subTitle = benefit.values[statusKey] ?? '';
    const perkItem: GmRewardPerk = {
      id: benefit.id,
      action: availability === 'available' ? benefit.action : undefined,
      link: availability === 'available' ? benefit.link : undefined,
      img: benefit.perkImg,
      type: benefit.type,
      title: benefit.title,
      subTitle,
      isDisabled: !subTitle,
    };

    return { perkItem, availability, lockBadge: badges.statuses, action: benefit.action };
  });
};
